import { useCallback, useEffect, useState } from 'react'
import Keycloak from 'keycloak-js'
import type { User, AuthState } from '@/types/auth'
import { setKeycloakInstance, api } from '@/lib/api'
import { getBaseDomain } from '@/lib/subdomain'

interface MfaStatusResponse {
  mfaEnabled: boolean
  hasSecret: boolean
  mfaVerifiedThisSession: boolean
}

interface UserProfileResponse {
  id: string
  email: string
  fullName: string
  roles: string[]
  isActive: boolean
}

/**
 * Get Keycloak configuration.
 * For subdomain access, uses the base domain's /auth path to ensure cookies work properly.
 */
function getKeycloakConfig() {
  // For subdomain access, route through the base domain's nginx proxy
  // This ensures Keycloak cookies are properly scoped to the base domain
  const baseDomain = getBaseDomain()
  const protocol = window.location.protocol

  // Always use the base domain for Keycloak to ensure cookie sharing
  const keycloakUrl = `${protocol}//${baseDomain}/auth`

  return {
    url: keycloakUrl,
    realm: import.meta.env.VITE_KEYCLOAK_REALM || 'sage',
    clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'sage-frontend',
  }
}

/**
 * Get the silent check SSO redirect URI for the current origin.
 * Each subdomain uses its own silent-check-sso.html to avoid cross-origin issues.
 */
function getSilentCheckSsoRedirectUri(): string {
  return `${window.location.origin}/silent-check-sso.html`
}

// Singleton Keycloak instance
let keycloakInstance: Keycloak | null = null
let isInitialized = false
let initPromise: Promise<boolean> | null = null

function getKeycloak(): Keycloak {
  if (!keycloakInstance) {
    const config = getKeycloakConfig()
    console.log('Creating Keycloak instance with config:', config)
    keycloakInstance = new Keycloak(config)
  }
  return keycloakInstance
}

export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    error: null,
    mfaPending: false,
  })

  const keycloak = getKeycloak()

  const extractUser = useCallback((): User | null => {
    if (!keycloak.tokenParsed) return null

    const token = keycloak.tokenParsed as {
      sub?: string
      email?: string
      name?: string
      preferred_username?: string
      realm_access?: { roles?: string[] }
      roles?: string[]
    }

    return {
      id: token.sub || '',
      email: token.email || token.preferred_username || '',
      fullName: token.name || token.preferred_username || '',
      roles: token.roles || token.realm_access?.roles || [],
    }
  }, [keycloak])

  const initialize = useCallback(async () => {
    // Prevent double initialization (React StrictMode calls effects twice)
    if (isInitialized) {
      console.log('Keycloak already initialized, using existing state')
      // Don't override mfaPending state on re-initialization
      return
    }

    // If initialization is already in progress, wait for it
    if (initPromise) {
      console.log('Keycloak initialization in progress, waiting...')
      try {
        await initPromise
        // Don't set state here - let the original initialization handle it
      } catch (error) {
        console.error('Keycloak init error:', error)
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          error: 'Failed to initialize authentication.',
          mfaPending: false,
        })
      }
      return
    }

    try {
      console.log('Initializing Keycloak...')

      initPromise = keycloak.init({
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: getSilentCheckSsoRedirectUri(),
        checkLoginIframe: false, // Disabled for cross-subdomain compatibility
        pkceMethod: 'S256',
      })

      const authenticated = await initPromise
      isInitialized = true

      // Set Keycloak instance for API client
      setKeycloakInstance(keycloak)

      console.log('Keycloak initialized, authenticated:', authenticated)

      if (authenticated) {
        // Fetch the user profile from backend to get the database user ID
        // The token's sub claim is the Keycloak ID, but we need the database ID
        // for comparing with data created_by fields
        let user: User | null = null
        try {
          const profileResponse = await api.get<UserProfileResponse>('/auth/me')
          user = {
            id: profileResponse.data.id,
            email: profileResponse.data.email,
            fullName: profileResponse.data.fullName,
            roles: profileResponse.data.roles,
          }
        } catch (error) {
          console.error('Failed to fetch user profile:', error)
          // Fall back to extracting from token if profile fetch fails
          user = extractUser()
        }

        // Check MFA status from backend (tracks verification per Keycloak session)
        try {
          const mfaResponse = await api.get<MfaStatusResponse>('/auth/mfa/status')
          const { mfaEnabled, mfaVerifiedThisSession } = mfaResponse.data

          if (mfaEnabled && !mfaVerifiedThisSession) {
            // MFA is enabled but not verified for this session
            console.log('MFA verification required')
            setAuthState({
              isAuthenticated: false,  // Not fully authenticated yet
              isLoading: false,
              user,  // Keep user info for display
              error: null,
              mfaPending: true,  // Show MFA verification dialog
            })
          } else {
            // No MFA required, or already verified this session
            console.log('MFA not required or already verified')
            setAuthState({
              isAuthenticated: true,
              isLoading: false,
              user,
              error: null,
              mfaPending: false,
            })
          }
        } catch (error) {
          console.error('Failed to check MFA status:', error)
          // Fail closed: require MFA verification if status check fails
          setAuthState({
            isAuthenticated: true,
            isLoading: false,
            user,
            error: null,
            mfaPending: true,
          })
        }
      } else {
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          error: null,
          mfaPending: false,
        })
      }
    } catch (error) {
      console.error('Keycloak init error:', error)
      initPromise = null
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        error: 'Failed to initialize authentication.',
        mfaPending: false,
      })
    }
  }, [keycloak, extractUser])

  useEffect(() => {
    initialize()

    // Set up token refresh
    keycloak.onTokenExpired = () => {
      keycloak.updateToken(30).catch(() => {
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          error: 'Session expired',
          mfaPending: false,
        })
      })
    }
  }, [initialize, keycloak])

  const login = useCallback((redirectPath?: string) => {
    // Use current origin for redirect so user stays on the same subdomain
    // Handle case where React event is passed (e.g., from onClick)
    // Default to /studies so users can select a study after login
    const path = typeof redirectPath === 'string' ? redirectPath : '/studies'
    keycloak.login({
      redirectUri: window.location.origin + path,
    })
  }, [keycloak])

  const logout = useCallback(() => {
    // Logout redirects to the main domain (studies list)
    const baseDomain = getBaseDomain()
    const protocol = window.location.protocol
    const port = window.location.port

    let redirectUri = `${protocol}//${baseDomain}`
    if (port && port !== '80' && port !== '443') {
      redirectUri += `:${port}`
    }

    keycloak.logout({
      redirectUri,
    })
  }, [keycloak])

  const getToken = useCallback(async (): Promise<string | null> => {
    if (!keycloak.authenticated) return null

    try {
      await keycloak.updateToken(30)
      return keycloak.token || null
    } catch {
      return null
    }
  }, [keycloak])

  const hasRole = useCallback(
    (role: string): boolean => {
      return authState.user?.roles.includes(role) || false
    },
    [authState.user]
  )

  // Verify MFA code after Keycloak authentication
  const verifyMfa = useCallback(async (code: string, isBackupCode: boolean = false): Promise<boolean> => {
    try {
      await api.post('/auth/mfa/verify-login', { code, is_backup_code: isBackupCode })
      // MFA verification successful - backend stores session ID

      // Fetch the user profile to ensure we have the database user ID
      let user = authState.user
      try {
        const profileResponse = await api.get<UserProfileResponse>('/auth/me')
        user = {
          id: profileResponse.data.id,
          email: profileResponse.data.email,
          fullName: profileResponse.data.fullName,
          roles: profileResponse.data.roles,
        }
      } catch {
        // Keep existing user info if profile fetch fails
      }

      // Complete authentication
      setAuthState(prev => ({
        ...prev,
        isAuthenticated: true,
        mfaPending: false,
        error: null,
        user,
      }))
      return true
    } catch (error) {
      console.error('MFA verification failed:', error)
      setAuthState(prev => ({
        ...prev,
        error: 'Invalid verification code',
      }))
      return false
    }
  }, [authState.user])

  // Cancel MFA verification and logout
  const cancelMfa = useCallback(() => {
    // Reset state and logout from Keycloak
    setAuthState({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      error: null,
      mfaPending: false,
    })
    keycloak.logout({
      redirectUri: window.location.origin,
    })
  }, [keycloak])

  return {
    ...authState,
    login,
    logout,
    getToken,
    hasRole,
    verifyMfa,
    cancelMfa,
  }
}
